// src/Components/Signup/Signup.js
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Signup.css";

import SignUpLoading from "../Loading/SignUpLoading";
import client from "../../api/client";

export default function Signup() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const safeTrim = (s) => (typeof s === "string" ? s.trim() : s);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    const trimmedName = safeTrim(name);
    const trimmedEmail = safeTrim(email);
    const trimmedPhone = safeTrim(phone);
    const trimmedCity = safeTrim(city);
    const trimmedPassword = password;

    if (!trimmedName || !trimmedEmail || !trimmedPhone || !trimmedPassword) {
      setErrorMsg("Please fill all required fields.");
      return;
    }
    if (trimmedPassword.length < 6) {
      setErrorMsg("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);

    let geo = null;
    try {
      geo = await new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(
          (p) => resolve({ location_lat: p.coords.latitude, location_lng: p.coords.longitude }),
          () => resolve(null),
          { timeout: 5000 }
        );
      });
    } catch (err) {
      geo = null;
    }

    try {
      const payload = {
        name: trimmedName,
        email: trimmedEmail,
        phone: trimmedPhone,
        password: trimmedPassword,
        ...(trimmedCity ? { city: trimmedCity } : {}),
        ...(geo || {}),
      };

      const res = await client.post("/api/auth/signup", payload);
      const data = res.data;

      if (!data || data.ok === false) {
        const backendError = data?.error || data?.message || "Signup failed";
        setErrorMsg(typeof backendError === "string" ? backendError : JSON.stringify(backendError));
        return;
      }

      if (data.token) localStorage.setItem("token", data.token);
      if (data.user) localStorage.setItem("user", JSON.stringify(data.user));

      alert("Signup successful! Redirecting to login...");
      navigate("/login");
    } catch (err) {
      console.error("Signup failed:", err);
      const serverMsg = err?.response?.data?.error || err?.response?.data?.message;
      setErrorMsg(serverMsg || "Network error — please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <SignUpLoading />}
      <div className="signup-container">
        <div className="signup-card">
          {/* Header Section */}
          <div className="signup-header">
            <div className="logo-container">
              <img src="/logo1.png" alt="Old Machine Market" className="signup-logo" />
            </div>
            <h1 className="signup-title">Create Account</h1>
            <p className="signup-subtitle">Join Old Machine Market today</p>
          </div>

          {/* Form Section */}
          <form onSubmit={handleSubmit} className="signup-form">
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                Full Name *
              </label>
              <input
                id="name"
                className="form-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                name="name"
                required
                placeholder="Enter your full name"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email Address *
              </label>
              <input
                id="email"
                className="form-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                name="email"
                required
                placeholder="you@example.com"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone" className="form-label">
                Phone Number *
              </label>
              <input
                id="phone"
                className="form-input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                name="phone"
                required
                placeholder="+91XXXXXXXXXX"
                pattern="[0-9+() -]{7,20}"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="city" className="form-label">
                City
              </label>
              <input
                id="city"
                className="form-input"
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                name="city"
                placeholder="Enter your city (optional)"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <div className="password-header">
                <label htmlFor="password" className="form-label">
                  Password *
                </label>
                <button 
                  type="button" 
                  className="show-password-btn"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              <input
                id="password"
                className="form-input"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                name="password"
                required
                minLength={6}
                placeholder="At least 6 characters"
                disabled={loading}
              />
            </div>

            {errorMsg && (
              <div className="error-message" role="alert">
                {errorMsg}
              </div>
            )}

            <button 
              className="signup-button" 
              disabled={loading} 
              type="submit"
            >
              {loading ? (
                <>
                  <div className="button-spinner"></div>
                  Creating Account...
                </>
              ) : (
                "Create Account"
              )}
            </button>
          </form>

          {/* Footer Section */}
          <div className="signup-footer">
            <p className="login-text">
              Already have an account?{" "}
              <Link to="/login" className="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}